/**
 * Contexto automático que acompaña al feedback: en qué pantalla estaba el usuario cuando lo mandó.
 *
 * Es lo que ahorra las tres idas y vueltas de "¿dónde te pasó?". Dos reglas que no se negocian:
 *
 * 1. **`pathname`, nunca `href`.** Los query strings del Explorador y de las vistas guardadas
 *    llevan ids; mandamos las CLAVES del query (sirven para reproducir) sin sus valores.
 * 2. **Si el usuario destilda el checkbox, esto no viaja.** Mandar el contexto igual "para el
 *    triage" convierte al checkbox en una mentira, y es exactamente lo que rompe la confianza de
 *    quien nos manda feedback. El test guardián verifica que la key `context` no exista en el
 *    FormData.
 */

const CLIENT_ID_RE = /\/client\/(\d+)/;
const BUSINESS_UNIT_ID_RE = /\/business-unit\/(\d+)/;

function matchId(pathname, regex) {
    const match = regex.exec(pathname || '');
    return match ? Number(match[1]) : null;
}

function queryKeys(search) {
    if (!search) return [];
    try {
        return Array.from(new URLSearchParams(search).keys());
    } catch (_) {
        return [];
    }
}

export function collectFeedbackContext({screen, uiVersion, experienceTier} = {}) {
    const location = typeof window !== 'undefined' ? window.location : {pathname: '', search: ''};
    const pathname = location.pathname || '';

    return {
        screen: screen || '',
        path: pathname,
        query_keys: queryKeys(location.search),
        client_id: matchId(pathname, CLIENT_ID_RE),
        business_unit_id: matchId(pathname, BUSINESS_UNIT_ID_RE),
        viewport: {
            w: typeof window !== 'undefined' ? window.innerWidth : null,
            h: typeof window !== 'undefined' ? window.innerHeight : null,
            dpr: typeof window !== 'undefined' ? window.devicePixelRatio : null,
        },
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        locale: typeof navigator !== 'undefined' ? navigator.language : '',
        timezone: resolveTimezone(),
        occurred_at: new Date().toISOString(),
        ui_version: uiVersion || '',
        experience_tier: experienceTier || '',
    };
}

function resolveTimezone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch (_) {
        return '';
    }
}

/**
 * Las filas que el usuario ve al desplegar "Ver qué se envía".
 *
 * Se construyen DESDE EL OBJETO REAL que se va a mandar, no desde una lista escrita a mano: así la
 * explicación no puede desincronizarse de los datos y decir algo que no es cierto.
 *
 * Las filas que no aplican se omiten — nada de "—" que ocupe lugar sin decir nada.
 */
export function describeContextForUser(context, t) {
    if (!context) return [];
    const rows = [];

    if (context.path) {
        rows.push({label: t('feedback.context.row_screen'), value: context.path});
    }
    if (context.client_id) {
        rows.push({label: t('feedback.context.row_client'), value: `#${context.client_id}`});
    }
    if (context.business_unit_id) {
        rows.push({label: t('feedback.context.row_bu'), value: `#${context.business_unit_id}`});
    }
    if (context.viewport && context.viewport.w) {
        rows.push({
            label: t('feedback.context.row_viewport'),
            value: `${context.viewport.w} × ${context.viewport.h}`,
        });
    }
    if (context.user_agent) {
        rows.push({label: t('feedback.context.row_browser'), value: describeBrowser(context.user_agent)});
    }
    return rows;
}

/** Nombre legible del navegador. El user agent crudo lo parsea el backend; acá se muestra corto. */
function describeBrowser(userAgent) {
    const ua = userAgent || '';
    const browser = /Edg\//.test(ua) ? 'Edge'
        : /Chrome\//.test(ua) ? 'Chrome'
        : /Firefox\//.test(ua) ? 'Firefox'
        : /Safari\//.test(ua) ? 'Safari'
        : '';
    const os = /Windows/.test(ua) ? 'Windows'
        : /Mac OS X/.test(ua) ? 'macOS'
        : /Android/.test(ua) ? 'Android'
        : /iPhone|iPad/.test(ua) ? 'iOS'
        : /Linux/.test(ua) ? 'Linux'
        : '';
    if (browser && os) return `${browser} · ${os}`;
    return browser || os || ua.slice(0, 40);
}
